// src/components/Navbar.tsx (Clerk-alapú bejelentkezés-észleléssel)
import React, { useState, useEffect, useContext } from "react";
import { ThemeContext } from '../contexts/ThemeContext';
import {
    AppBar,
    Toolbar,
    Button,
    IconButton,
    Box,
    Menu,
    MenuItem,
    Typography,
    useMediaQuery,
    useTheme,
    Switch,
    Avatar,
    Backdrop,
    CircularProgress,
} from "@mui/material";
import { styled } from "@mui/system";
// ÚJ: Clerk hookok a Cookies helyett
import { useUser, useClerk } from "@clerk/clerk-react";
import { Link as RouterLink, LinkProps as RouterLinkProps, useLocation, useNavigate } from "react-router-dom";
import { useIsAdmin } from "../hooks/useIsAdmin";
import StarImage from "../assets/images/logo.jpeg";

import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import MenuIcon from "@mui/icons-material/Menu";

const StyledAppBar = styled(AppBar)(({ theme }) => ({
    background: "rgba(18, 10, 57, 0.85)",
    backdropFilter: "blur(10px)",
    boxShadow: "0 4px 30px rgba(0, 0, 0, 0.1)",
}));

interface NavButtonProps {
    component: React.ElementType<RouterLinkProps>;
    to: RouterLinkProps['to'];
    state?: { isLogin: boolean };
}

const NavButton = styled(Button)<NavButtonProps & { active: boolean }>(({ theme, active }) => ({
    margin: "0 6px",
    borderRadius: "12px",
    padding: "6px 14px",
    textTransform: "none",
    fontSize: "0.95rem",
    fontWeight: active ? 600 : 500,
    transition: "all 0.25s ease-in-out",
    backgroundColor: active
        ? (theme.palette.mode === 'dark' ? 'rgba(252, 198, 50, 0.16)' : 'rgba(0, 0, 0, 0.08)')
        : 'transparent',
    color: active
        ? (theme.palette.mode === 'dark' ? '#FCC632' : '#6D1B77')
        : 'inherit',
    "&:hover": {
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
        transform: "translateY(-1px)",
    },
}));

const CompanyName = styled(Typography)({
    fontFamily: "Montserrat, sans-serif",
    color: "inherit",
    marginLeft: "12px",
    fontWeight: 600,
});

const LogoImg = styled("img")({
    height: 32,
    borderRadius: "50%",
});

const ThemeToggle = styled(Switch)(({ theme }) => ({
    width: 62,
    height: 34,
    padding: 7,
    "& .MuiSwitch-switchBase": {
        margin: 8,
        padding: 0,
        transform: "translateX(0px)",
        transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&.Mui-checked": {
            transform: "translateX(28px)",
            color: "#fff",
            "& .MuiSwitch-thumb": {
                backgroundColor: "#2c3e50",
                boxShadow: "0 0 10px rgba(0,0,0,0.5)",
            },
            "& + .MuiSwitch-track": {
                opacity: 1,
                background: "linear-gradient(to right, #2c3e50, #3498db)",
            },
        },
        "& .MuiSwitch-thumb": {
            backgroundColor: "#FCC632",
            boxShadow: "0 0 10px rgba(255, 215, 0, 0.7)",
        },
    },
    "& .MuiSwitch-track": {
        borderRadius: 11,
        opacity: 1,
        background: "linear-gradient(to right, #ffe603fd, #8a8701ff)",
        boxSizing: "border-box",
    },
    "& .MuiSwitch-thumb": {
        width: 22,
        height: 22,
        boxShadow: "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "background-color 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    },
}));

const Navbar = () => {
    const themeContext = useContext(ThemeContext);
    const darkMode = themeContext?.darkMode ?? false;
    const toggleDarkMode = themeContext?.toggleDarkMode ?? (() => {});
    const [anchorElMenu, setAnchorElMenu] = useState<null | HTMLElement>(null);
    const [anchorElAccount, setAnchorElAccount] = useState<null | HTMLElement>(null);
    const [scrolled, setScrolled] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const location = useLocation();
    const navigate = useNavigate();

    // ÚJ: a bejelentkezési állapotot a Clerk adja (nem a sütik)
    const { isLoaded, isSignedIn, user } = useUser();
    const { signOut } = useClerk();

    const isUserLoggedIn = isLoaded && !!isSignedIn;
    // Az admin jogot a backend adja meg (admin_users tábla), nem a Clerk
    const { isAdmin, checked: adminChecked } = useIsAdmin();
    // A Clerk saját profilképét használjuk, így nem kell külön /api/user/me hívás
    const userAvatarUrl: string | null = isUserLoggedIn ? (user?.imageUrl ?? null) : null;

    // ÚJ: töltőképernyő kijelentkezéskor
    const [loggingOut, setLoggingOut] = useState(false);

    const handleLogout = async () => {
        setAnchorElAccount(null);
        setLoggingOut(true);                                   // 1) töltő képernyő be
        await new Promise((resolve) => setTimeout(resolve, 800)); // hogy látszódjon
        try {
            // 2) a Clerk kijelentkeztet, és UTÁNA / oldalra dob
            await signOut({ redirectUrl: "/" });
        } finally {
            setLoggingOut(false);                              // 3) töltő le
        }
    };

    // Jogosultság kiírása a konzolra (a backend válasza alapján)
    useEffect(() => {
        if (!isLoaded || !adminChecked) return;
        if (isSignedIn) {
            //console.log("Bejelentkezett felhasználó jogosultsága:", isAdmin ? "ADMIN" : "USER");
        } else {
            //console.log("Nincs bejelentkezett felhasználó");
        }
    }, [isLoaded, adminChecked, isSignedIn, isAdmin]);

    const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElMenu(event.currentTarget);
    };

    const handleAccountMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElAccount(event.currentTarget);
    };

    const handleCloseMenu = () => {
        setAnchorElMenu(null);
    };

    const handleCloseAccountMenu = () => {
        setAnchorElAccount(null);
    };

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const menuItems = [
        { label: "Főoldal", path: "/" },
        { label: "Magánóra", path: "/maganora" },
        { label: "Gyermekagykontroll", path: "/gyermekagykontroll" },
        { label: "Tanulási Tréning", path: "/tanulasitrening" },
        { label: "Chillag", path: "/chillag" },
        { label: "Újdonságok", path: "/ujdonsagok" },
        { label: "AI🤖", path: "/ai" },
    ];

    interface AccountMenuItem {
        label: string;
        path: string;
        state?: { isLogin: boolean };
        onClick?: () => void;
    }

    const accountMenuItems: AccountMenuItem[] = isUserLoggedIn
        ? [
            { label: "Saját profil", path: "/profile" },
            { label: "Kijelentkezés", path: "/", onClick: handleLogout },
        ]
        : [
            { label: "Regisztráció", path: "/authpages", state: { isLogin: false } },
            { label: "Bejelentkezés", path: "/authpages", state: { isLogin: true } },
        ];

    if (isUserLoggedIn && isAdmin) {
        accountMenuItems.push({
            label: "Admin Dashboard",
            path: "/admin"
        });
    }

    return (
        <>
        <Backdrop
            open={loggingOut}
            sx={{ zIndex: (t) => t.zIndex.modal + 1, color: "#FCC632", backgroundColor: "rgba(0,0,0,0.75)" }}
        >
            <CircularProgress color="inherit" />
        </Backdrop>
        <StyledAppBar
            position="fixed"
            sx={{
                transition: "0.3s ease",
                background: scrolled
                    ? darkMode
                        ? "rgba(18, 10, 57, 0.95)"
                        : "#F3E6F6"
                    : darkMode
                        ? "rgba(18, 10, 57, 0.85)"
                        : "rgba(243, 230, 246, 0.85)",
                color: darkMode ? "#fff" : "#000",
            }}
        >
            <Toolbar>
                <Box display="flex" alignItems="center" flexGrow={1}>
                    <IconButton
                        edge="start"
                        color="inherit"
                        sx={{ p: 0 }}
                        component={RouterLink}
                        to="/"
                    >
                        <LogoImg src={StarImage} alt="Logo" />
                    </IconButton>
                    <CompanyName variant="h6">Csillagtanoda</CompanyName>
                </Box>

                {!isMobile ? (
                    // Desktop View
                    <Box display="flex" alignItems="center">
                        {menuItems.map((item) => (
                            <NavButton
                                key={item.path}
                                component={RouterLink}
                                to={item.path}
                                active={location.pathname === item.path}
                            >
                                {item.label}
                            </NavButton>
                        ))}

                        <ThemeToggle
                            checked={darkMode}
                            onChange={toggleDarkMode}
                            icon={<LightModeIcon sx={{ fontSize: 18, color: "rgba(255, 255, 255, 1)" }} />}
                            checkedIcon={<DarkModeIcon sx={{ fontSize: 16, color: "#fff" }} />}
                        />

                        <IconButton
                            color="inherit"
                            onClick={handleAccountMenu}
                            sx={{
                                ml: 2,
                                p: '8px',
                                borderRadius: '12px',
                                backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
                                color: darkMode ? '#FCC632' : '#6D1B77',
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                    backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)',
                                    transform: 'translateY(-1px)',
                                }
                            }}
                        >
                            {userAvatarUrl ? (
                                <Avatar
                                    src={userAvatarUrl}
                                    sx={{ width: 28, height: 28, border: `2px solid ${darkMode ? '#FCC632' : '#6D1B77'}` }}
                                />
                            ) : (
                                <AccountCircleIcon sx={{ fontSize: '24px' }} />
                            )}
                        </IconButton>
                        <Menu
                            anchorEl={anchorElAccount}
                            open={Boolean(anchorElAccount)}
                            onClose={handleCloseAccountMenu}
                            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                            transformOrigin={{ vertical: "top", horizontal: "right" }}
                            disableScrollLock
                            sx={{
                                "& .MuiPaper-root": {
                                    borderRadius: "14px",
                                    mt: 1.5,
                                    boxShadow: "0 10px 30px rgba(0,0,0,0.3)",
                                    background: darkMode ? "rgba(18, 10, 57, 0.97)" : "rgba(255, 255, 255, 0.95)",
                                    backdropFilter: "blur(12px)",
                                    minWidth: "160px",
                                    border: darkMode ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(0, 0, 0, 0.05)",
                                },
                            }}
                        >
                            {accountMenuItems.map((item, index) => (
                                <MenuItem
                                    key={`${item.path}-${index}`}
                                    component={item.onClick ? 'li' : RouterLink}
                                    to={item.onClick ? undefined : item.path}
                                    state={item.state}
                                    onClick={(e: React.MouseEvent) => {
                                        handleCloseAccountMenu();
                                        if (item.onClick) {
                                            e.preventDefault();
                                            item.onClick();
                                        }
                                    }}
                                    sx={{
                                        fontSize: '0.95rem',
                                        py: 1.2,
                                        px: 2,
                                        margin: '4px 8px',
                                        borderRadius: '8px',
                                        color: darkMode ? '#fff' : '#000',
                                        transition: 'background-color 0.2s ease',
                                        '&:hover': {
                                            backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.05)',
                                        }
                                    }}
                                >
                                    {item.label}
                                </MenuItem>
                            ))}
                        </Menu>
                    </Box>
                ) : (
                    // Mobile View
                    <Box display="flex" alignItems="center">
                        <IconButton
                            color="inherit"
                            onClick={handleAccountMenu}
                            sx={{
                                mr: 1,
                                p: '8px',
                                borderRadius: '12px',
                                backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
                                color: darkMode ? '#FCC632' : '#6D1B77'
                            }}
                        >
                            {userAvatarUrl ? (
                                <Avatar
                                    src={userAvatarUrl}
                                    sx={{ width: 28, height: 28, border: `2px solid ${darkMode ? '#FCC632' : '#6D1B77'}` }}
                                />
                            ) : (
                                <AccountCircleIcon sx={{ fontSize: '24px' }} />
                            )}
                        </IconButton>
                        <Menu
                            anchorEl={anchorElAccount}
                            open={Boolean(anchorElAccount)}
                            onClose={handleCloseAccountMenu}
                            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                            transformOrigin={{ vertical: "top", horizontal: "right" }}
                            disableScrollLock
                            sx={{
                                "& .MuiPaper-root": {
                                    borderRadius: "14px",
                                    mt: 1.5,
                                    background: darkMode ? "rgba(18, 10, 57, 0.97)" : "rgba(255, 255, 255, 0.95)",
                                    backdropFilter: "blur(12px)",
                                    minWidth: "160px",
                                    border: darkMode ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(0, 0, 0, 0.05)",
                                },
                            }}
                        >
                            {accountMenuItems.map((item, index) => (
                                <MenuItem
                                    key={`${item.path}-${index}`}
                                    component={item.onClick ? 'li' : RouterLink}
                                    to={item.onClick ? undefined : item.path}
                                    state={item.state}
                                    onClick={(e: React.MouseEvent) => {
                                        handleCloseAccountMenu();
                                        if (item.onClick) {
                                            e.preventDefault();
                                            item.onClick();
                                        }
                                    }}
                                    sx={{
                                        fontSize: '0.95rem',
                                        py: 1.2,
                                        px: 2,
                                        margin: '4px 8px',
                                        borderRadius: '8px',
                                        color: darkMode ? '#fff' : '#000',
                                        '&:hover': {
                                            backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.05)',
                                        }
                                    }}
                                >
                                    {item.label}
                                </MenuItem>
                            ))}
                        </Menu>

                        <ThemeToggle
                            checked={darkMode}
                            onChange={toggleDarkMode}
                            icon={<LightModeIcon sx={{ fontSize: 18, color: "rgba(255, 255, 255, 1)" }} />}
                            checkedIcon={<DarkModeIcon sx={{ fontSize: 16, color: "#fff" }} />}
                        />
                        <IconButton
                            color="inherit"
                            aria-label="menu"
                            onClick={handleMenu}
                            sx={{
                                color: darkMode ? "#FCC632" : "#6D1B77",
                                p: '8px',
                                borderRadius: '12px',
                                ml: 1,
                                backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
                            }}
                        >
                            <MenuIcon />
                        </IconButton>
                        <Menu
                            anchorEl={anchorElMenu}
                            open={Boolean(anchorElMenu)}
                            onClose={handleCloseMenu}
                            anchorOrigin={{ vertical: "top", horizontal: "right" }}
                            transformOrigin={{ vertical: "top", horizontal: "right" }}
                            sx={{
                                "& .MuiPaper-root": {
                                    borderRadius: "16px",
                                    mt: 6,
                                    background: darkMode
                                        ? "rgba(18, 10, 57, 0.97)"
                                        : "rgba(255, 255, 255, 0.95)",
                                    backdropFilter: "blur(12px)",
                                    minWidth: "220px",
                                    boxShadow: "0 10px 30px rgba(0,0,0,0.3)",
                                    border: darkMode ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(0, 0, 0, 0.05)",
                                },
                            }}
                        >
                            {menuItems.map((item) => (
                                <MenuItem
                                    key={item.path}
                                    component={RouterLink}
                                    to={item.path}
                                    onClick={handleCloseMenu}
                                    sx={{
                                        py: 1.2,
                                        px: 2.5,
                                        margin: '4px 8px',
                                        borderRadius: '8px',
                                        color: darkMode ? "#fff" : "#000",
                                        "&:hover": {
                                            background: darkMode ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.05)",
                                        },
                                        ...(location.pathname === item.path && {
                                            color: darkMode ? '#FCC632' : '#6D1B77',
                                            fontWeight: 'bold',
                                            backgroundColor: darkMode ? 'rgba(252, 198, 50, 0.16)' : 'rgba(0, 0, 0, 0.08)',
                                        }),
                                    }}
                                >
                                    {item.label}
                                </MenuItem>
                            ))}
                        </Menu>
                    </Box>
                )}
            </Toolbar>
        </StyledAppBar>
        </>
    );
};

export default Navbar;